import { useEffect, useMemo, useRef } from 'react';

import {
    isCommunicationsLayerBlock,
    isLegacyScheduleBlock,
    type CommunicationBlockDefinition,
    type CommunicationBlockIdValue,
} from './communicationBlocks';
import {
    cardKey,
    type CommunicationCardKey,
    type CommunicationCardState,
} from './communicationCardState';
import { resolveLegacyScheduleEmailEnabled } from './communicationsLegacyManagerEmailPersistence';
import {
    resolveCommunicationBlockEnabled,
    resolveCommunicationEmailContent,
} from './communicationsPersistence';

import type { useGetCycleEvaluation } from '@/api/cycle-evaluation-services/cycle-evaluation';
import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { CommunicationEmailAudienceValue } from '@/pages/CycleEvaluation/Communications/communicationEmailAudience';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';
import type { EmailDefaults } from '@/utils/emailDefaults360';
import type { MutableRefObject } from 'react';

import { useDebounce } from '@/hooks/useDebounce';
import { CommunicationEmailAudience } from '@/pages/CycleEvaluation/Communications/communicationEmailAudience';

const AUTOSAVE_DELAY_MS = 500;

const ENABLED_ONLY_PROBE_DEFAULTS: EmailDefaults = { title: '', body: '' };
const ENABLED_ONLY_PROBE_LANGUAGE = 'en';

type BlockEnabledByBlockId = Partial<Record<CommunicationBlockIdValue, boolean>>;
type CardsByKey = Partial<Record<CommunicationCardKey, CommunicationCardState>>;
type LegacyScheduleQueryByEvaluationType = Partial<
    Record<EvaluationTypeValue, ReturnType<typeof useGetCycleEvaluation>>
>;

export const buildSwitchesSignature = ({
    blockEnabled,
    cards,
}: {
    blockEnabled: BlockEnabledByBlockId;
    cards: CardsByKey;
}): string => {
    const entries = [
        ...Object.keys(blockEnabled).map(
            (id) => `block:${id}:${blockEnabled[id as CommunicationBlockIdValue]}`,
        ),
        ...Object.keys(cards).map((key) => `${key}:${cards[key as CommunicationCardKey]?.enabled}`),
    ];

    return entries.sort().join('|');
};

const buildServerSwitchesSignature = ({
    visibleBlocks,
    group,
    legacyScheduleQueryByEvaluationType,
}: {
    visibleBlocks: CommunicationBlockDefinition[];
    group: CycleEvaluationGroup | undefined;
    legacyScheduleQueryByEvaluationType: LegacyScheduleQueryByEvaluationType;
}): string => {
    const entries: string[] = [];

    for (const block of visibleBlocks) {
        if (isLegacyScheduleBlock(block)) {
            const legacySchedule =
                legacyScheduleQueryByEvaluationType[block.evaluationType]?.data?.cycleSchedule;
            const managerEnabled = resolveLegacyScheduleEmailEnabled(
                legacySchedule,
                CommunicationEmailAudience.MANAGER,
            );
            const employeeEnabled =
                block.hasEmployeeCard &&
                resolveLegacyScheduleEmailEnabled(
                    legacySchedule,
                    CommunicationEmailAudience.EMPLOYEE,
                );

            // The two blocks carry no switch of their own; the block entry unions both emails.
            entries.push(`block:${block.id}:${managerEnabled || employeeEnabled}`);
            entries.push(
                `${cardKey(block.id, CommunicationEmailAudience.MANAGER)}:${managerEnabled}`,
            );

            if (block.hasEmployeeCard) {
                entries.push(
                    `${cardKey(block.id, CommunicationEmailAudience.EMPLOYEE)}:${employeeEnabled}`,
                );
            }

            continue;
        }

        if (!isCommunicationsLayerBlock(block)) {
            continue;
        }

        entries.push(
            `block:${block.id}:${resolveCommunicationBlockEnabled(group?.communications, block.id)}`,
        );

        const resolveEnabled = (audience: CommunicationEmailAudienceValue) =>
            resolveCommunicationEmailContent({
                communications: group?.communications,
                blockId: block.id,
                audience,
                language: ENABLED_ONLY_PROBE_LANGUAGE,
                defaults: ENABLED_ONLY_PROBE_DEFAULTS,
            }).enabled;

        entries.push(
            `${cardKey(block.id, CommunicationEmailAudience.MANAGER)}:${resolveEnabled(CommunicationEmailAudience.MANAGER)}`,
        );

        if (block.hasEmployeeCard) {
            entries.push(
                `${cardKey(block.id, CommunicationEmailAudience.EMPLOYEE)}:${resolveEnabled(CommunicationEmailAudience.EMPLOYEE)}`,
            );
        }
    }

    return entries.sort().join('|');
};

interface UseAutosaveCommunicationSwitchesParams {
    isReadOnly: boolean;
    hasUnsavedSwitchChanges: boolean;
    cycleEvaluationGroupId: string | undefined;
    hydratedRef: MutableRefObject<boolean>;
    visibleBlocks: CommunicationBlockDefinition[];
    group: CycleEvaluationGroup | undefined;
    legacyScheduleQueryByEvaluationType: LegacyScheduleQueryByEvaluationType;
    blockEnabled: BlockEnabledByBlockId;
    cards: CardsByKey;
    isPersisting: boolean;
    isPersistingRef: MutableRefObject<boolean>;
    lastWrittenSwitchesSignatureRef: MutableRefObject<string | undefined>;
    persistCommunications: () => Promise<boolean>;
    onAutosaved: () => void;
}

export const useAutosaveCommunicationSwitches = ({
    isReadOnly,
    hasUnsavedSwitchChanges,
    cycleEvaluationGroupId,
    hydratedRef,
    visibleBlocks,
    group,
    legacyScheduleQueryByEvaluationType,
    blockEnabled,
    cards,
    isPersisting,
    isPersistingRef,
    lastWrittenSwitchesSignatureRef,
    persistCommunications,
    onAutosaved,
}: UseAutosaveCommunicationSwitchesParams) => {
    const switchesSignature = useMemo(
        () => buildSwitchesSignature({ blockEnabled, cards }),
        [blockEnabled, cards],
    );
    const debouncedSignature = useDebounce(switchesSignature, AUTOSAVE_DELAY_MS);

    const latestRef = useRef({
        isReadOnly,
        hasUnsavedSwitchChanges,
        cycleEvaluationGroupId,
        visibleBlocks,
        group,
        legacyScheduleQueryByEvaluationType,
        persistCommunications,
        onAutosaved,
    });
    latestRef.current = {
        isReadOnly,
        hasUnsavedSwitchChanges,
        cycleEvaluationGroupId,
        visibleBlocks,
        group,
        legacyScheduleQueryByEvaluationType,
        persistCommunications,
        onAutosaved,
    };

    useEffect(() => {
        const {
            isReadOnly: readOnly,
            hasUnsavedSwitchChanges: stillUnsaved,
            cycleEvaluationGroupId: groupId,
            visibleBlocks: currentVisibleBlocks,
            group: currentGroup,
            legacyScheduleQueryByEvaluationType: currentLegacyScheduleQueryByEvaluationType,
            persistCommunications: persist,
            onAutosaved: notifyAutosaved,
        } = latestRef.current;

        if (
            !stillUnsaved ||
            readOnly ||
            !groupId ||
            !hydratedRef.current ||
            isPersistingRef.current
        ) {
            return;
        }

        if (debouncedSignature === lastWrittenSwitchesSignatureRef.current) {
            return;
        }

        const serverSignature = buildServerSwitchesSignature({
            visibleBlocks: currentVisibleBlocks,
            group: currentGroup,
            legacyScheduleQueryByEvaluationType: currentLegacyScheduleQueryByEvaluationType,
        });

        if (debouncedSignature === serverSignature) {
            return;
        }

        let cancelled = false;

        void persist().then((saved) => {
            if (!cancelled && saved) {
                notifyAutosaved();
            }
        });

        return () => {
            cancelled = true;
        };
    }, [
        debouncedSignature,
        hydratedRef,
        isPersisting,
        isPersistingRef,
        lastWrittenSwitchesSignatureRef,
    ]);
};

export { AUTOSAVE_DELAY_MS as COMMUNICATION_SWITCHES_AUTOSAVE_DELAY_MS };
